// src/hooks/useTimeBoxDetail.ts
// TimeBoxDetail 組件的核心狀態管理 Hook - 實現 Local-first 樂觀更新

import { useState, useEffect, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import { TimeBox, TimeBoxCategory } from '../../../types/timeBox';
import {
  createTimeBox,
  updateTimeBox,
  deleteTimeBox as deleteTimeBoxApi,
  getTimeBoxById as getTimeBoxByIdFromFirestore,
} from '../../../services/api/timeBoxes';
import { getCurrentUserId } from '../../../services/auth';
import { eventBus, EVENTS } from '../../../utils/eventBus';
import { logError } from '../../../utils/logger';
import {
  getTimeBoxById,
  updateTimeBoxLocal,
  updateTaskLocal,
  getTaskById,
  deleteTimeBoxLocal,
  createTimeBoxLocal,
} from '../../../services/indexedDB';
import { taskService } from '../../task';
import { computeLastTimeboxedDate } from '../../calendar/utils/timeBoxCreation';
import {
  calculateDuration,
  calculateNewEndTime
} from '../../../utils/timeBoxHelpers';
import { DRAG_CONFIG } from '../../../utils/calendarHelper';

// ============================================
// 類型定義
// ============================================

export interface UseTimeBoxDetailOptions {
  timeBoxId: string | null;
  isOpen: boolean;
  onClose?: () => void;
  onRefresh?: () => void;
  showSuccess: (title: string, message: string) => void;
  showError: (title: string, message: string) => void;
  mode?: 'create' | 'edit';
}

export interface UseTimeBoxDetailReturn {
  // 狀態
  timeBox: TimeBox | null;
  isLoading: boolean;
  error: string | null;
  isSubmitting: boolean;

  // 更新函數
  updateTitle: (title: string) => Promise<void>;
  updateNotes: (notes: string | undefined) => Promise<void>;
  updateStartTime: (startTime: Date) => Promise<void>;
  updateEndTime: (endTime: Date) => Promise<void>;
  updateCategory: (category: TimeBoxCategory | undefined) => Promise<void>;
  updateLocation: (location: string | undefined) => Promise<void>;
  updateTaskIds: (taskIds: string[]) => Promise<void>;

  // 操作
  deleteTimeBox: () => Promise<void>;
  saveTimeBox: (timeBox: TimeBox) => Promise<void>;
}

// ============================================
// Hook 實現
// ============================================

export function useTimeBoxDetail(options: UseTimeBoxDetailOptions): UseTimeBoxDetailReturn {
  const {
    timeBoxId,
    isOpen,
    onClose,
    onRefresh,
    showSuccess,
    showError,
    mode = 'edit'
  } = options;

  // 核心狀態
  const [timeBox, setTimeBox] = useState<TimeBox | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Refs 用於樂觀更新回滾
  const originalTimeBoxRef = useRef<TimeBox | null>(null);

  // 記錄原始 duration（用於時間智能調整）
  const originalDurationRef = useRef<number>(0);

  // ============================================
  // 數據加載
  // ============================================

  const fetchTimeBox = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // 1. 從 IndexedDB 快取讀取（快速顯示）
      const cachedTimeBox = await getTimeBoxById(id);
      if (cachedTimeBox) {
        setTimeBox(cachedTimeBox);
        originalTimeBoxRef.current = cachedTimeBox;
        originalDurationRef.current = calculateDuration(
          cachedTimeBox.startTime,
          cachedTimeBox.endTime
        );
        setIsLoading(false);
        return;
      }

      // 2. IndexedDB 沒有，從 Firestore 讀取
      const firestoreTimeBox = await getTimeBoxByIdFromFirestore(id);
      if (firestoreTimeBox) {
        setTimeBox(firestoreTimeBox);
        originalTimeBoxRef.current = firestoreTimeBox;
        originalDurationRef.current = calculateDuration(
          firestoreTimeBox.startTime,
          firestoreTimeBox.endTime
        );

        // 3. 立即寫入 IndexedDB（確保後續更新不會失敗）
        try {
          await createTimeBoxLocal(firestoreTimeBox);
        } catch (cacheError) {
          logError('TimeBoxDetail', '寫入 IndexedDB 失敗', cacheError);
          // 快取失敗不阻止顯示
        }
      } else {
        setError('TimeBox not found');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
      logError('TimeBox 獲取失敗', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 監聽 timeBoxId 變化
  useEffect(() => {
    if (isOpen && timeBoxId) {
      fetchTimeBox(timeBoxId);
    } else {
      setTimeBox(null);
      setError(null);
      originalTimeBoxRef.current = null;
      originalDurationRef.current = 0;
    }
  }, [isOpen, timeBoxId, fetchTimeBox]);

  // ============================================
  // 樂觀更新輔助函數
  // ============================================

  const optimisticUpdate = useCallback(async <T extends Partial<TimeBox>>(
    updates: T,
    apiCall: () => Promise<void>,
    successMessage?: { title: string; message: string }
  ): Promise<void> => {
    if (!timeBox) return;

    // 保存原始狀態用於回滾
    const originalTimeBox = { ...timeBox };
    originalTimeBoxRef.current = originalTimeBox;

    // 1. 樂觀更新本地狀態
    const updatedTimeBox = { ...timeBox, ...updates };
    setTimeBox(updatedTimeBox);

    // 2. 更新 IndexedDB
    try {
      await updateTimeBoxLocal(timeBox.id, updates);
    } catch (cacheError) {
      logError('IndexedDB 更新失敗', cacheError);
      // 快取失敗不阻止流程
    }

    try {
      // 3. 同步到 Firestore
      await apiCall();

      // 4. 發布事件通知其他組件
      eventBus.publish(EVENTS.TIMEBOX.UPDATED, timeBox.id, updatedTimeBox);

      // 5. 顯示成功消息
      if (successMessage) {
        showSuccess(successMessage.title, successMessage.message);
      }

      // 6. 刷新父組件
      if (onRefresh) {
        onRefresh();
      }

    } catch (error) {
      // 6. 回滾本地狀態
      setTimeBox(originalTimeBox);

      // 7. 回滾 IndexedDB
      try {
        await updateTimeBoxLocal(timeBox.id, originalTimeBox);
      } catch (revertError) {
        logError('IndexedDB 回滾失敗', revertError);
      }

      logError('TimeBox 更新失敗', error);
      showError('Update Failed', 'Failed to update time box. Please try again.');
      throw error;
    }
  }, [timeBox, showSuccess, showError, onRefresh]);

  // ============================================
  // 更新函數
  // ============================================

  const updateTitle = useCallback(async (title: string) => {
    if (!timeBox) return;

    await optimisticUpdate(
      { title },
      async () => {
        const userId = getCurrentUserId();
        await updateTimeBox({
          id: timeBox.id,
          title,
          userId
        });
      }
    );
  }, [timeBox, optimisticUpdate]);

  const updateNotes = useCallback(async (notes: string | undefined) => {
    if (!timeBox) return;

    await optimisticUpdate(
      { notes },
      async () => {
        const userId = getCurrentUserId();
        await updateTimeBox({
          id: timeBox.id,
          notes,
          userId
        });
      }
    );
  }, [timeBox, optimisticUpdate]);

  const updateStartTime = useCallback(async (startTime: Date) => {
    if (!timeBox) return;

    // 基於原有 duration 計算新的結束時間
    const newEndTime = calculateNewEndTime(startTime, originalDurationRef.current);

    await optimisticUpdate(
      { startTime, endTime: newEndTime },
      async () => {
        const userId = getCurrentUserId();
        await updateTimeBox({
          id: timeBox.id,
          startTime,
          endTime: newEndTime,
          userId
        });
      }
    );
  }, [timeBox, optimisticUpdate]);

  const updateEndTime = useCallback(async (endTime: Date) => {
    if (!timeBox) return;

    // 驗證：End Time 必須晚於 Start Time
    if (endTime <= timeBox.startTime) {
      showError('Invalid Time', 'End time must be later than start time');
      return;
    }

    // 驗證：最小時長 15 分鐘
    const newDuration = calculateDuration(timeBox.startTime, endTime);
    if (newDuration < DRAG_CONFIG.MIN_DURATION_MINUTES) {
      showError('Invalid Duration', 'Must be at least 15 minutes');
      return;
    }

    // 更新 duration
    originalDurationRef.current = newDuration;

    await optimisticUpdate(
      { endTime },
      async () => {
        const userId = getCurrentUserId();
        await updateTimeBox({
          id: timeBox.id,
          endTime,
          userId
        });
      }
    );
  }, [timeBox, optimisticUpdate, showError]);

  const updateCategory = useCallback(async (category: TimeBoxCategory | undefined) => {
    if (!timeBox) return;

    await optimisticUpdate(
      { category },
      async () => {
        const userId = getCurrentUserId();
        await updateTimeBox({
          id: timeBox.id,
          category,
          userId
        });
      }
    );
  }, [timeBox, optimisticUpdate]);

  const updateLocation = useCallback(async (location: string | undefined) => {
    if (!timeBox) return;

    await optimisticUpdate(
      { location },
      async () => {
        const userId = getCurrentUserId();
        await updateTimeBox({
          id: timeBox.id,
          location,
          userId
        });
      }
    );
  }, [timeBox, optimisticUpdate]);

  const updateTaskIds = useCallback(async (taskIds: string[]) => {
    if (!timeBox) return;

    // 在 optimisticUpdate 前捕捉舊值（閉包中 timeBox 仍是更新前的狀態）
    const oldTaskIds = timeBox.taskIds || [];
    const timeBoxDate = format(timeBox.startTime, 'yyyy-MM-dd');
    const addedTaskIds = taskIds.filter(id => !oldTaskIds.includes(id));
    const removedTaskIds = oldTaskIds.filter(id => !taskIds.includes(id));

    await optimisticUpdate(
      { taskIds },
      async () => {
        const userId = getCurrentUserId();
        await updateTimeBox({
          id: timeBox.id,
          taskIds,
          userId
        });
      }
    );

    // Local-first: 更新受影響 Tasks 的 scheduledTimeBoxIds + lastTimeboxedDate
    // 新增關聯的 tasks
    for (const taskId of addedTaskIds) {
      try {
        const task = await getTaskById(taskId);
        if (!task) continue;
        const currentScheduledIds = task.scheduledTimeBoxIds || [];
        if (currentScheduledIds.includes(timeBox.id)) continue;
        const currentLast = task.lastTimeboxedDate;
        const newLastTimeboxedDate = !currentLast || timeBoxDate > currentLast ? timeBoxDate : currentLast;
        await updateTaskLocal(taskId, {
          scheduledTimeBoxIds: [...currentScheduledIds, timeBox.id],
          lastTimeboxedDate: newLastTimeboxedDate,
          updatedAt: new Date(),
        });
        await taskService.addScheduledTimeBox(taskId, timeBox.id);
        await taskService.updateLastTimeboxedDate(taskId, newLastTimeboxedDate);
      } catch (err) {
        logError(`Trigger 3: 更新新增 Task ${taskId} 失敗`, err);
      }
    }

    // 移除關聯的 tasks
    for (const taskId of removedTaskIds) {
      try {
        const task = await getTaskById(taskId);
        if (!task) continue;
        const remainingIds = (task.scheduledTimeBoxIds || []).filter(id => id !== timeBox.id);
        const newLastTimeboxedDate = await computeLastTimeboxedDate(remainingIds);
        await updateTaskLocal(taskId, {
          scheduledTimeBoxIds: remainingIds,
          lastTimeboxedDate: newLastTimeboxedDate ?? null,
          updatedAt: new Date(),
        });
        await taskService.removeScheduledTimeBox(taskId, timeBox.id, newLastTimeboxedDate);
      } catch (err) {
        logError(`Trigger 3: 更新移除 Task ${taskId} 失敗`, err);
      }
    }
  }, [timeBox, optimisticUpdate]);

  // ============================================
  // 刪除操作
  // ============================================

  const handleDeleteTimeBox = useCallback(async () => {
    if (!timeBox) return;

    setIsSubmitting(true);

    // 刪除前先捕捉關聯的 taskIds
    const affectedTaskIds = timeBox.taskIds || [];

    try {
      // 1. 立即從 IndexedDB 刪除 TimeBox
      await deleteTimeBoxLocal(timeBox.id);

      // 2. 重算並更新每個關聯 Task 的 scheduledTimeBoxIds + lastTimeboxedDate（IndexedDB）
      const taskUpdates = new Map<string, string | undefined>();
      for (const taskId of affectedTaskIds) {
        const task = await getTaskById(taskId);
        if (!task) continue;
        const remainingIds = (task.scheduledTimeBoxIds || []).filter(id => id !== timeBox.id);
        const newLastTimeboxedDate = await computeLastTimeboxedDate(remainingIds);
        taskUpdates.set(taskId, newLastTimeboxedDate);
        await updateTaskLocal(taskId, {
          scheduledTimeBoxIds: remainingIds,
          lastTimeboxedDate: newLastTimeboxedDate ?? null,
          updatedAt: new Date(),
        });
      }

      // 3. 同步到 Firestore（TimeBox 刪除 + 更新關聯 Tasks）
      await deleteTimeBoxApi(timeBox.id);
      for (const taskId of affectedTaskIds) {
        await taskService.removeScheduledTimeBox(taskId, timeBox.id, taskUpdates.get(taskId));
      }

      // 4. 發布事件
      eventBus.publish(EVENTS.TIMEBOX.DELETED, timeBox.id);

      // 5. 顯示成功消息
      showSuccess('Deleted', 'Time box deleted successfully');

      // 6. 刷新父組件
      if (onRefresh) {
        onRefresh();
      }

      // 7. 關閉 modal
      if (onClose) {
        onClose();
      }

    } catch (error) {
      logError('TimeBox 刪除失敗', error);
      showError('Delete Failed', 'Failed to delete time box. Please try again.');
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [timeBox, showSuccess, showError, onRefresh, onClose]);

  // ============================================
  // 保存操作（用於創建和整體更新）
  // ============================================

  const saveTimeBox = useCallback(async (updatedTimeBox: TimeBox) => {
    setIsSubmitting(true);

    try {
      const userId = getCurrentUserId();
      const isNewTimeBox = updatedTimeBox.id.startsWith('temp-');

      if (isNewTimeBox && mode === 'create') {
        // 創建新 TimeBox
        await createTimeBoxLocal(updatedTimeBox);

        // 同步到 Firestore
        const result = await createTimeBox({
          ...updatedTimeBox,
          userId
        });

        // 發布事件
        eventBus.publish(EVENTS.TIMEBOX.CREATED, result);

        showSuccess('Created', 'Time box created successfully');
      } else {
        // 更新現有 TimeBox
        await updateTimeBoxLocal(updatedTimeBox.id, updatedTimeBox);

        await updateTimeBox({
          ...updatedTimeBox,
          id: updatedTimeBox.id,
          userId
        });

        eventBus.publish(EVENTS.TIMEBOX.UPDATED, updatedTimeBox.id, updatedTimeBox);

        showSuccess('Updated', 'Time box updated successfully');
      }

      // 刷新父組件
      if (onRefresh) {
        onRefresh();
      }

      // 關閉 modal
      if (onClose) {
        onClose();
      }

    } catch (error) {
      logError('TimeBox 保存失敗', error);
      showError('Save Failed', 'Failed to save time box. Please try again.');
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [mode, showSuccess, showError, onRefresh, onClose]);

  // ============================================
  // 返回值
  // ============================================

  return {
    // 狀態
    timeBox,
    isLoading,
    error,
    isSubmitting,

    // 更新函數
    updateTitle,
    updateNotes,
    updateStartTime,
    updateEndTime,
    updateCategory,
    updateLocation,
    updateTaskIds,

    // 操作
    deleteTimeBox: handleDeleteTimeBox,
    saveTimeBox,
  };
}
